/**
 * useActiveAlarmCount Hook
 *
 * Returns the count of active (non-resolved) alarms for the badge display.
 * Scoped to the currently selected property.
 *
 * @module features/alarms/hooks
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/services/supabase/client';
import { useAuth } from '@/services/auth/AuthProvider';
import { useOptionalSelectedProperty } from '@/features/properties/hooks/useSelectedProperty';
import type { AlarmSeverity } from '../types/alarms.types';

interface ActiveAlarmCounts {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

async function fetchActiveAlarmCounts(propertyId: string): Promise<ActiveAlarmCounts> {
  const { data, error } = await supabase
    .from('alarms')
    .select('severity')
    .eq('object_id', propertyId)
    .in('status', ['active', 'acknowledged']);

  if (error) {
    console.error('Error fetching active alarm counts:', error);
    return { total: 0, critical: 0, high: 0, medium: 0, low: 0 };
  }

  const alarms = data || [];
  const counts: ActiveAlarmCounts = {
    total: alarms.length,
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
  };

  for (const alarm of alarms) {
    const severity = alarm.severity as AlarmSeverity;
    if (severity in counts) {
      counts[severity]++;
    }
  }

  return counts;
}

/**
 * Hook that returns active alarm counts for badge display,
 * scoped to the user's selected property.
 */
export function useActiveAlarmCount() {
  const { user } = useAuth();
  const selectedPropertyId = useOptionalSelectedProperty()?.selectedPropertyId;

  const {
    data: counts = { total: 0, critical: 0, high: 0, medium: 0, low: 0 },
    isLoading,
  } = useQuery<ActiveAlarmCounts>({
    queryKey: ['alarms', 'activeCount', selectedPropertyId],
    queryFn: () => fetchActiveAlarmCounts(selectedPropertyId!),
    enabled: !!user && !!selectedPropertyId,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: true,
  });

  const highestSeverity: 'critical' | 'warning' | 'info' | null =
    counts.critical > 0
      ? 'critical'
      : counts.high > 0 || counts.medium > 0
        ? 'warning'
        : counts.low > 0
          ? 'info'
          : null;

  return { counts, isLoading, highestSeverity };
}
